import React from 'react';
import { TooltipProps } from 'recharts';

export type TooltipRow = {
  key: string;
  label: string;
  color: string;
  value: number | string;
};

interface ChartTooltipBoxProps {
  title: string;
  rows: TooltipRow[];
}

export const ChartTooltipBox = ({ title, rows }: ChartTooltipBoxProps) => (
  <div className="bg-backGroundGray rounded-lg px-3 py-2 shadow-lg text-sm min-w-[140px]">
    <p className="text-mainWhite font-semibold mb-1">{title}</p>
    {rows.map((row) => (
      <p key={row.key} className="flex items-center gap-2 text-textGray">
        <span
          className="inline-block w-2.5 h-2.5 rounded-sm"
          style={{ backgroundColor: row.color }}
        />
        {row.label}
        <span className="ml-auto pl-3 text-mainWhite tabular-nums">
          {row.value}
        </span>
      </p>
    ))}
  </div>
);

type RechartsTooltipProps = TooltipProps<number, string> & {
  getTitle: (payload: any) => string;
  labels: Record<string, string>;
  /** Extra rows shown under the series, e.g. the day's weather. */
  getExtraRows?: (payload: any) => TooltipRow[];
};

/**
 * Tooltip content for recharts bar charts: one row per series that has a
 * label. Other series (e.g. weather) can be added through getExtraRows.
 */
export const RechartsTooltip = ({
  active,
  payload,
  getTitle,
  labels,
  getExtraRows,
}: RechartsTooltipProps) => {
  if (!active || !payload?.length) {
    return null;
  }

  const rows = payload
    .filter((item) => labels[String(item.dataKey)])
    .reverse()
    .map((item) => ({
      key: String(item.dataKey),
      label: labels[String(item.dataKey)],
      color: String(item.color),
      value: item.value ?? 0,
    }));

  const extraRows = getExtraRows ? getExtraRows(payload[0].payload) : [];

  return (
    <ChartTooltipBox
      title={getTitle(payload[0].payload)}
      rows={[...rows, ...extraRows]}
    />
  );
};
